import { auth } from "firebase-config";

export interface Building {
  id: string;
  organizationId: string;
  name: string;
  address: string;
  indiri?: string; // address from backend (Indirizzo)
  citind?: string; // city from backend (Città)
  descri?: string; // optional description returned by API
  createdAt: string; 
  displayName?: string; 
  last_versamenti_csv_download_at?: string;
  last_bank_file_tx_min_date?: string;
  last_bank_file_tx_max_date?: string;
  is_enabled?: boolean; // building status: enabled/disabled
}

interface CreateBuildingPayload {
    name: string;
    address: string;
    organizationId: string;
}

// Building Status Management Interfaces
export interface BuildingStatusUpdate {
  building_id: string;
  is_enabled: boolean;
}

export interface UpdateBuildingStatusRequest {
  buildings: BuildingStatusUpdate[];
}

export interface UpdateBuildingStatusResponse {
  updated_count: number;
}

export interface UpdateBuildingStatusError {
  message: string;
  errors: string[];
  updated_count: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getAuthHeader = async (forceRefresh: boolean = false) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated");
  }
  const token = await user.getIdToken(forceRefresh);
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

export const parseBuildingDisplayName = (buildingId: string): string => {
  const parts = buildingId.split('_');
  if (parts.length > 1) {
    return parts[1]; 
  }
  return buildingId; 
};

export const getBuildings = async (forceRefreshToken: boolean = false): Promise<Building[]> => {
  try {
    const headers = await getAuthHeader(forceRefreshToken);
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings`, {
      headers,
    });

    if (!response.ok) {
      switch (response.status) {
        case 401:
          throw new Error('Invalid or missing token');
        case 403:
          throw new Error('Insufficient role/permissions');
        case 429:
          throw new Error('Rate limited');
        case 500:
        case 502:
        case 503:
        case 504:
          throw new Error('Server error');
        default:
          throw new Error(`Failed to fetch buildings: ${response.status}`);
      }
    }

    const buildings = await response.json();
    
    return buildings.map((building: Building) => ({
      ...building,
      displayName: building.name || parseBuildingDisplayName(building.id)
    }));
  } catch (error) {
    console.error('Error fetching buildings:', error);
    throw error;
  }
};

export const createBuilding = async (payload: CreateBuildingPayload): Promise<Building> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error('Failed to create building');
    }

    return response.json();
}

export const deleteBuilding = async (buildingId: string): Promise<void> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}`, {
        method: 'DELETE',
        headers,
    });

    if (!response.ok) {
        throw new Error('Failed to delete building');
    }
}

export const updateBuildingStatusBulk = async (buildings: BuildingStatusUpdate[]): Promise<UpdateBuildingStatusResponse> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/status`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ buildings }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update building status');
    }

    return response.json();
}

export interface BuildingUnit {
  id: string;
  cod_wstabi: string;
  cod_ui: string;
  descr1: string;
  buildingId: string;
  organizationId: string;
  createdAt: any;
  updatedAt: any;
}

export interface Person {
  id: string;
  cod_person: string;
  nome1: string;
  nome2: string;
  nome3: string;
  person_type: 'proprietario' | 'inquilino';
  buildingId: string;
  organizationId: string;
  createdAt: any;
  updatedAt: any;
}

export interface PersonWithUnits extends Person {
  units: Array<{
    cod_ui: string;
    cod_proprietario: string;
    cod_inquilino: string;
    cod_wstabi: string;
    descr1: string;
  }>;
}

export interface Gestione {
  id: string;
  cod_gestione: string;
  descri: string;
  descr_cong: string;
  tipoge: string;
  chiuso?: string | number | boolean; // '0' or false => open
  buildingId: string;
  organizationId: string;
  createdAt: any;
  updatedAt: any;
}

export interface Rate {
  id: string;
  num_rata: number;
  imp_tot: number;
  paid: number;
  cod_gestione: string;
  cod_ui: string;
  cod_person: string;
  buildingId: string;
  organizationId: string;
  createdAt: any;
  updatedAt: any;
  status?: 'paid' | 'unpaid';
}

export interface DropdownData {
  units: BuildingUnit[];
  persons: PersonWithUnits[];
  gestione: Gestione[];
  rates: Rate[];
}

export const buildingsApi = {
  async getBuildingUnits(buildingId: string): Promise<BuildingUnit[]> {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}/units`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch building units: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getPersons(buildingId: string, unitId?: string, tipo?: 'proprietario' | 'inquilino'): Promise<PersonWithUnits[]> {
    const headers = await getAuthHeader();
    const params = new URLSearchParams();
    if (unitId) params.append('unitId', unitId);
    if (tipo) params.append('tipo', tipo);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}/persons?${params}`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch persons: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getPersonByNomeOrCod(buildingId: string, nome1?: string, codPerson?: string): Promise<PersonWithUnits | null> {
    const headers = await getAuthHeader();
    const params = new URLSearchParams();
    if (nome1) params.append('nome1', nome1);
    if (codPerson) params.append('codPerson', codPerson);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}/persons/search?${params}`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to search person: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getOpenGestione(buildingId: string): Promise<Gestione[]> {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}/gestione/open`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch open gestione: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getUnpaidRates(buildingId: string, gestioneId: string, unitId?: string): Promise<Rate[]> {
    const headers = await getAuthHeader();
    const params = new URLSearchParams();
    if (unitId) params.append('unitId', unitId);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}/gestione/${gestioneId}/rates?${params}`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch unpaid rates: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getDropdownData(buildingId: string): Promise<DropdownData> {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}/dropdown-data`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch dropdown data: ${response.statusText}`);
    }
    
    return response.json();
  },

  async postVersamentiDownloadEvent(buildingId: string): Promise<void> {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/buildings/${buildingId}/versamenti/download-event`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) {
      throw new Error(`Failed to record download event: ${response.statusText}`);
    }
  }
}; 